/* ================================
   FACET FILTERS BASE (Shopify Standard)
   ================================ */

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document.querySelectorAll('#ProductGridContainer .scroll-trigger').forEach((el) =>
      el.classList.add('scroll-trigger--cancel')
    );
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const count = parsed.getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((s) => s.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // remove obsolete facets
    Array.from(facetDetailsFromDom).forEach((current) => {
      if (!Array.from(facetDetailsFromFetch).some(({ id }) => current.id === id)) current.remove();
    });

    const matchesId = (el) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? el.id === jsFilter.id : false;
    };

    const toRender = Array.from(facetDetailsFromFetch).filter((el) => !matchesId(el));
    const countsToRender = Array.from(facetDetailsFromFetch).find(matchesId);

    toRender.forEach((el, i) => {
      const current = document.getElementById(el.id);
      if (current) {
        current.innerHTML = el.innerHTML;
      } else {
        if (i > 0) {
          const prev = toRender[i - 1];
          if (el.className === prev.className) {
            document.getElementById(prev.id).after(el);
            return;
          }
        }
        if (el.parentElement) {
          document.querySelector(`#${el.parentElement.id} .js-filter`).before(el);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    // 🔄 Re-init Range Sliders after AJAX update
    if (typeof window.initRmRange === 'function') window.initRmRange();

    if (countsToRender) {
      const closestJSFilterID = event?.target?.closest('.js-filter')?.id;
      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
      }
    }
  }

  static renderActiveFacets(html) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach((sel) => {
      const newEl = html.querySelector(sel);
      if (!newEl) return;
      document.querySelector(sel).innerHTML = newEl.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
    mobileSelectors.forEach((s) => {
      if (!html.querySelector(s)) return;
      document.querySelector(s).innerHTML = html.querySelector(s).innerHTML;
    });
    const mForm = document.getElementById('FacetFiltersFormMobile');
    if (mForm) mForm.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const tSummary = target.querySelector('.facets__summary');
    const sSummary = source.querySelector('.facets__summary');
    if (sSummary && tSummary) tSummary.outerHTML = sSummary.outerHTML;

    const tHeader = target.querySelector('.facets__header');
    const sHeader = source.querySelector('.facets__header');
    if (sHeader && tHeader) tHeader.outerHTML = sHeader.outerHTML;

    const tWrap = target.querySelector('.facets-wrap');
    const sWrap = source.querySelector('.facets-wrap');
    if (sWrap && tWrap) tWrap.outerHTML = sWrap.outerHTML;
  }

  static renderMobileCounts(source, target) {
    const tList = target.querySelector('.mobile-facets__list');
    const sList = source.querySelector('.mobile-facets__list');
    if (sList && tList) tList.outerHTML = sList.outerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [{ section: document.getElementById('product-grid').dataset.id }];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const params = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(params, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';
      sortForms.forEach((f) => {
        if (!isMobile) {
          if (['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'].includes(f.id)) {
            forms.push(this.createSearchParams(f));
          }
        } else if (f.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(f));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* ================================
   PRICE RANGE (Shopify default)
   ================================ */

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((el) => {
      el.addEventListener('change', this.onRangeChange.bind(this));
      el.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

/* ================================
   FACET REMOVE (Shopify default)
   ================================ */

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const link = this.querySelector('a');
    link.setAttribute('role', 'button');
    link.addEventListener('click', this.closeFilter.bind(this));
    link.addEventListener('keyup', (e) => {
      e.preventDefault();
      if (e.code.toUpperCase() === 'SPACE') this.closeFilter(e);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}
customElements.define('facet-remove', FacetRemove);

window.initRmRange = function initRmRange() {
  document.querySelectorAll('[data-range-filter]').forEach((wrap) => {
    if (wrap.dataset.rmInitialized === '1') return; // mehrfach-Init vermeiden
    const minAttr = Number(wrap.getAttribute('data-min'));
    const maxAttr = Number(wrap.getAttribute('data-max'));
    const label   = (wrap.getAttribute('data-filter-label') || '').toLowerCase();
    const storeKey = 'rmRange:' + label;

    const minInput = wrap.querySelector('.rm-range--min');
    const maxInput = wrap.querySelector('.rm-range--max');
    const minOut   = wrap.querySelector('.rm-min-val');
    const maxOut   = wrap.querySelector('.rm-max-val');
    const applyBtn = wrap.querySelector('.rm-range-apply');
    const track    = wrap.querySelector('.rm-range-controls');
    if (!minInput || !maxInput || !track) return;

    // Bounds & Step
    [minInput, maxInput].forEach((inp) => {
      inp.min = isNaN(minAttr) ? 0 : minAttr;
      inp.max = isNaN(maxAttr) ? 100 : maxAttr;
      inp.step = 1;
    });

    // Zugehörige Checkboxen/Form
    const details = wrap.closest('details');
    if (!details) return;
    const checkboxes = [...details.querySelectorAll('input[type="checkbox"]')];
    const form = details.closest('form');

    const normalize = (s) => Number(String(s).replace(',', '.'));
    const clampOrder = () => {
      if (+minInput.value > +maxInput.value) {
        const t = minInput.value;
        minInput.value = maxInput.value;
        maxInput.value = t;
      }
    };

    // ---------- Wiederherstellung der Werte ----------
    // 1) aktiv gesetzte Checkboxen aus dem DOM (serverseitig korrekt)
    const activeVals = checkboxes.filter(cb => cb.checked).map(cb => normalize(cb.value));
    // 2) ansonsten localStorage
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem(storeKey) || 'null'); } catch(e) {}

    if (activeVals.length >= 1) {
      minInput.value = Math.min(...activeVals);
      maxInput.value = Math.max(...activeVals);
    } else if (stored && isFinite(stored.min) && isFinite(stored.max)) {
      // innerhalb der Bounds clampen (für den Fall, dass sich Facet-Grenzen ändern)
      minInput.value = Math.max(minInput.min, Math.min(minInput.max, +stored.min));
      maxInput.value = Math.max(maxInput.min, Math.min(maxInput.max, +stored.max));
      clampOrder();
    } else {
      // Fallback: Facet-Bounds
      minInput.value = minInput.min;
      maxInput.value = maxInput.max;
    }

    // ---------- Live-Labels unter den Griffen ----------
    let bubbleMin = track.querySelector('.rm-value--min');
    let bubbleMax = track.querySelector('.rm-value--max');
    if (!bubbleMin) {
      bubbleMin = document.createElement('span');
      bubbleMin.className = 'rm-value rm-value--min';
      track.appendChild(bubbleMin);
    }
    if (!bubbleMax) {
      bubbleMax = document.createElement('span');
      bubbleMax.className = 'rm-value rm-value--max';
      track.appendChild(bubbleMax);
    }

    function positionBubbles() {
      const rect = track.getBoundingClientRect();
      const lo = +minInput.min, hi = +maxInput.max, span = hi - lo || 1;
      const pctMin = (+minInput.value - lo) / span;
      const pctMax = (+maxInput.value - lo) / span;
      bubbleMin.style.left = (pctMin * rect.width) + 'px';
      bubbleMax.style.left = (pctMax * rect.width) + 'px';
      bubbleMin.textContent = `${minInput.value} cm`;
      bubbleMax.textContent = `${maxInput.value} cm`;
    }

    const updateReadout = () => {
      clampOrder();
      if (minOut) minOut.textContent = minInput.value;
      if (maxOut) maxOut.textContent = maxInput.value;
      // nach Layout messen → zweifaches rAF vermeidet „Springen“
      requestAnimationFrame(() => requestAnimationFrame(positionBubbles));
    };

    // Initiales Ausrichten
    updateReadout();

    // Events
    const onInput = () => {
      updateReadout();
      // während der Interaktion live in storage schreiben (snappy UX)
      try {
        localStorage.setItem(storeKey, JSON.stringify({ min: +minInput.value, max: +maxInput.value }));
      } catch(e) {}
    };
    minInput.addEventListener('input', onInput);
    maxInput.addEventListener('input', onInput);
    window.addEventListener('resize', () => requestAnimationFrame(positionBubbles), { passive: true });

    // Anwenden → Checkboxen setzen → submit + Werte persistent speichern
    applyBtn?.addEventListener('click', () => {
      const lo = +minInput.value, hi = +maxInput.value;
      checkboxes.forEach(cb => {
        const v = normalize(cb.value);
        if (!isNaN(v)) cb.checked = (v >= lo && v <= hi);
      });
      try { localStorage.setItem(storeKey, JSON.stringify({ min: lo, max: hi })); } catch(e) {}
      if (form?.requestSubmit) form.requestSubmit(); else form?.submit();
    });

    // Markiere als initialisiert (verhindert doppelte Defaults)
    wrap.dataset.rmInitialized = '1';
  });
};
